import { useCallback, useMemo } from 'react';
import { getAllCompositeSegments, isSplitsInTimelineEnabledPure } from '@goldcast/player-config/src/utils/clipUtils';
import { LayoutType } from '@goldcast/player-config/src/types/asset-types';
import { CLIP_UPDATE_TYPE } from '../../ClipNewTimeline/clipUpdateDataUtils/constants';
import { updateClipData } from '../../ClipNewTimeline/clipUpdateDataUtils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import useDialog from '@/components/organisms/useDialog';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

/**
 * Shared selection logic for a single magic-layout option. Consumed by both the
 * list-row (`ClipCustomizerMagicLayoutOption`) and the card-grid
 * (`ClipCustomizerLayoutCard`) so both stay behavior-identical.
 */
export default function useMagicLayoutSelection(magicLayoutOption: LayoutType) {
  const { clipId, clipData } = useClipsContext();
  const { isOpen, openDialog, closeDialog } = useDialog();
  const splitsEnabled = isSplitsInTimelineEnabledPure({
    clip: clipData,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
  });

  const allSceneLayouts = useMemo(() => {
    if (splitsEnabled) {
      return getAllCompositeSegments(clipData).flatMap(compositeSegment =>
        compositeSegment.scenes.map(scene => scene.details.layout)
      );
    }
    return (clipData.asset_metadata.scenes ?? []).map(scene => scene.details.layout);
  }, [clipData, splitsEnabled]);

  // True when scenes disagree on layout (e.g. some GRID, some FOCUS). When every scene shares
  // the same layout, that layout should be shown as selected no matter how many scenes exist.
  const hasMixedSceneLayouts = useMemo(() => new Set(allSceneLayouts).size > 1, [allSceneLayouts]);

  const clipLayout = useMemo(() => {
    if (hasMixedSceneLayouts) {
      return clipData.asset_metadata.layout;
    }
    return allSceneLayouts[0] ?? clipData.asset_metadata.layout;
  }, [allSceneLayouts, hasMixedSceneLayouts, clipData]);

  const segmentHasMultipleScenes = useMemo(() => {
    if (splitsEnabled) {
      return !!getAllCompositeSegments(clipData).find(compositeSegment => compositeSegment.scenes.length > 1);
    }
    return (clipData.asset_metadata.scenes?.length ?? 0) > 1;
  }, [clipData, splitsEnabled]);

  const handleClick = useCallback(() => {
    if (hasMixedSceneLayouts || segmentHasMultipleScenes) {
      openDialog();
      return;
    }

    if (clipLayout === magicLayoutOption) {
      return;
    }

    updateClipData({
      clipId,
      actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_LAYOUT,
      layout: magicLayoutOption
    });
  }, [clipId, clipLayout, magicLayoutOption, hasMixedSceneLayouts, segmentHasMultipleScenes, openDialog]);

  const handleConfirm = useCallback(() => {
    updateClipData({
      clipId,
      actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_LAYOUT,
      layout: magicLayoutOption
    });
    closeDialog();
  }, [clipId, magicLayoutOption, closeDialog]);

  const handleCancel = useCallback(() => {
    closeDialog();
  }, [closeDialog]);

  const isSingleSceneSplits = splitsEnabled && !segmentHasMultipleScenes;

  const dialogContent =
    isSingleSceneSplits || magicLayoutOption !== 'AUDIOGRAM'
      ? 'This will update all the existing scenes to use the selected layout. Do you want to continue?'
      : 'This will delete all existing scenes in this segment and replace them with a single layout. Are you sure you want to continue?';

  return {
    isActive: !hasMixedSceneLayouts && clipLayout === magicLayoutOption,
    handleClick,
    isDialogOpen: isOpen,
    dialogContent,
    handleConfirm,
    handleCancel
  };
}
